import React, { useState } from 'react';
import { Save, ExternalLink, Globe, Phone, Mail } from 'lucide-react';
import { useContentStore } from './hooks/useContentStore';

interface LinkData {
  id: string;
  title: string;
  url: string;
  type: 'external' | 'internal' | 'email' | 'phone';
  description?: string;
}

interface CompanyLinks {
  social: LinkData[];
  contact: {
    phone: string;
    email: string;
    address: string;
    website: string;
  };
}

const LinkManager: React.FC = () => {
  const [links, setLinks] = useContentStore<CompanyLinks>('companyLinks', {
    social: [
      { id: '1', title: 'Facebook', url: 'https://facebook.com/bbhainsurance', type: 'external', description: 'Company Facebook page' },
      { id: '2', title: 'LinkedIn', url: 'https://linkedin.com/company/bbhainsurance', type: 'external', description: 'Professional LinkedIn page' }
    ],
    contact: {
      phone: '(555) 123-4567',
      email: 'info@bbhainsurance.com',
      address: '123 Main Street, Anytown, CA 12345',
      website: 'https://bbhainsurance.com'
    }
  });
  const [editingLink, setEditingLink] = useState<LinkData | null>(null);
  const [editingSection, setEditingSection] = useState<'social' | null>(null);

  const [linkForm, setLinkForm] = useState<Partial<LinkData>>({
    title: '',
    url: '',
    type: 'external',
    description: ''
  });

  const handleContactChange = (field: keyof CompanyLinks['contact'], value: string) => {
    setLinks(prev => ({
      ...prev,
      contact: {
        ...prev.contact,
        [field]: value
      }
    }));
  };

  const handleEditLink = (link: LinkData, section: 'social') => {
    setEditingLink(link);
    setEditingSection(section);
    setLinkForm(link);
  };

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingSection || !linkForm.url || !editingLink) return;

    setLinks(prev => ({
      ...prev,
      [editingSection]: prev[editingSection].map(link =>
        link.id === editingLink.id ? { ...link, url: linkForm.url } : link
      )
    }));

    setEditingLink(null);
    setEditingSection(null);
    setLinkForm({});
  };

  const handleCancelEdit = () => {
    setEditingLink(null);
    setEditingSection(null);
    setLinkForm({});
  };

  const getLinkIcon = (type: LinkData['type']) => {
    switch (type) {
      case 'email':
        return <Mail size={16} />;
      case 'phone':
        return <Phone size={16} />;
      case 'external':
        return <ExternalLink size={16} />;
      default:
        return <Globe size={16} />;
    }
  };

  return (
    <div className="min-w-0 p-4 sm:p-6">
      <div className="mb-6 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Contact Details & Links</h1>
          <p className="text-gray-600 mt-1">Update how visitors can contact you and find your social pages</p>
        </div>
        

      </div>

      <div className="space-y-8">
        {/* Contact Information */}
        <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl mb-6">Contact Information</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={links.contact.phone}
                onChange={(e) => handleContactChange('phone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={links.contact.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Address
              </label>
              <input
                type="text"
                value={links.contact.address}
                onChange={(e) => handleContactChange('address', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Link
              </label>
              <input
                type="url"
                value={links.contact.website}
                onChange={(e) => handleContactChange('website', e.target.value)}
                placeholder="https://yourdomain.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Social Media Links</h2>
            <p className="text-sm text-gray-600 mt-1">Update the links to your social media pages</p>
          </div>

          <div className="grid gap-4">
            {links.social.map((link) => (
              <div key={link.id} className="flex min-w-0 flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="shrink-0">{getLinkIcon(link.type)}</span>
                  <div className="min-w-0">
                    <h4 className="font-medium text-gray-900">{link.title}</h4>
                    <p className="break-all text-sm text-gray-600">{link.url}</p>
                    {link.description && (
                      <p className="text-sm text-gray-500">{link.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditLink(link, 'social')}
                    className="min-h-11 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-blue-600 transition-colors duration-200 hover:bg-blue-100"
                  >
                    Edit Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Link Form Modal */}
      {editingLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Edit {editingLink.title} Link
            </h3>
            
            <form onSubmit={handleLinkSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingLink.title} Link
                </label>
                <input
                  type="url"
                  required
                  value={linkForm.url || ''}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder={`https://${editingLink.title.toLowerCase()}.com/your-page`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3 pt-4 sm:flex sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="min-h-11 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  <Save size={16} />
                  <span>Save Link</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default LinkManager;
